import type {
  ComponentProps,
  MouseEventHandler,
} from 'react'
import { useCallback } from 'react'

import {
  tripSavedItemBookNowClicked,
  tripSavedItemOfferDetailsClicked,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import type { DetailItemCardVariant } from 'tripPlanner/components/DetailItemCard/common/types'
import useTripPlannerCTALabels from 'tripPlanner/hooks/useTripPlannerCTALabels'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import { useIntl } from 'react-intl'
import useRestoreCart from 'tripPlanner/hooks/useRestoreCart'
import type { LETripItem } from 'tripPlanner/types/tripItem'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'
import { getIsLuxPlusExclusiveAccess } from 'tripPlanner/utils/luxPlux'
import { addTripIdParam } from 'tripPlanner/utils/url'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'

interface Props {
  item: LETripItem
  className?: string
  variant: DetailItemCardVariant
  offerLink: string
  hotelOffer?:
    | App.HotelOffer
    | App.BedbankOffer
    | App.BundleOffer
    | App.VillaOffer
}

function LEOfferCTAs({
  item,
  className,
  variant,
  hotelOffer,
  offerLink,
}: Props) {
  const intl = useIntl()
  const canEdit = useCanEditTrip()
  const { searchFlightsCopy, viewOfferCopy } = useTripPlannerCTALabels()

  const { tripId } = item

  const { canRestoreCart, onClick } = useRestoreCart(item, offerLink, {
    hotel: hotelOffer,
  })
  const { setImmersiveMode } = useImmersiveModeHandlers()

  const isLuxPlusExclusiveAccess = getIsLuxPlusExclusiveAccess(item)

  const source =
    variant === 'drawer' ? 'trip_drawer_all_saved_items' : 'trip_summary'
  const modalElement = useModalElementContext()
  const onClickViewButton = useCallback<
    MouseEventHandler<HTMLButtonElement>
  >(() => {
    fireInteractionEvent(tripSavedItemOfferDetailsClicked(source))
    if (canEdit) {
      setImmersiveMode({
        tripId,
        sectionId: tripItemSelectionId(item.id),
      })
    }
    modalElement.resolve()
  }, [canEdit, item.id, tripId, modalElement, setImmersiveMode, source])

  // analytics event now fires unconditionally when this handler runs which is fine since its only wired to 'Book now' button
  const onClickRestoreCartButton = useCallback<
    MouseEventHandler<HTMLButtonElement>
  >(
    (e) => {
      fireInteractionEvent(tripSavedItemBookNowClicked(source))
      onClick(e)
    },
    [source, onClick],
  )

  const viewCopy =
    item.type === 'FLIGHT' ? searchFlightsCopy : viewOfferCopy

  const showBookNowButton = canRestoreCart && variant === 'drawer'

  let viewOfferButtonKind: ComponentProps<typeof TextButton>['kind'] =
    'primary'
  if (canEdit && showBookNowButton) {
    viewOfferButtonKind = 'tertiary'
  }

  return (
    <ProductPaletteProvider
      palette={isLuxPlusExclusiveAccess ? 'lux-plus' : 'default'}
    >
      <Group
        direction="horizontal"
        className={className}
        verticalAlign="center"
        horizontalAlign="stretch"
        tabletHorizontalAlign="space-between"
        gap={12}
      >
        <TextButton
          kind={viewOfferButtonKind}
          to={addTripIdParam(offerLink, tripId, canEdit)}
          target="_blank"
          onClick={onClickViewButton}
          size="medium"
        >
          {viewCopy}
        </TextButton>
        {showBookNowButton && (
          <TextButton
            kind="primary"
            onClick={onClickRestoreCartButton}
            size="medium"
          >
            {intl.formatMessage({
              id: 'SD9lmx2igxJdy9GA-9EE',
              defaultMessage: 'Book now',
              description: 'Button to book a saved trip item from the detail card',
            })}
          </TextButton>
        )}
      </Group>
    </ProductPaletteProvider>
  )
}

export default LEOfferCTAs
